import type { FeatureConfig, FooterConfig, HeaderConfig, ThemeConfig } from '../types/esusuSdk.d.ts';

// Color constants
export const COLORS = {
  PRIMARY_MAIN: '#0041D9',
  PRIMARY_LIGHT: '#4D7BFF',
  PRIMARY_DARK: '#002B8F',
  TEXT_PRIMARY: '#111116',
  TEXT_SECONDARY: '#7E7F8E',
  TEXT_TERTIARY: '#535364',
  BACKGROUND_WHITE: '#ffffff',
  BACKGROUND_LIGHT_GRAY: '#F5F5F5',
  BORDER_GRAY: '#7E7F8E',
  BORDER_LIGHT: '#e0e0e0',
  ERROR_RED: '#AC001E',
  SECONDARY_MAIN: '#7E7F8E',
  SECONDARY_LIGHT: '#A5A6B3',
  SECONDARY_DARK: '#5A5B68',
} as const;

// Typography constants
export const FONTS = {
  PRIMARY: 'Inter',
  HEADING: 'Object Sans',
  HEADING_URL:
    'https://www.zillowstatic.com/s3/constellation-website/public/shared/fonts/object-sans/LATEST/object-sans-heavy.woff2',
} as const;

// Shared theme configuration
export const theme: ThemeConfig = {
  palette: {
    primary: {
      main: COLORS.PRIMARY_MAIN,
      light: COLORS.PRIMARY_LIGHT,
      dark: COLORS.PRIMARY_DARK,
      contrastText: COLORS.BACKGROUND_WHITE,
    },
    secondary: {
      main: COLORS.SECONDARY_MAIN,
      light: COLORS.SECONDARY_LIGHT,
      dark: COLORS.SECONDARY_DARK,
    },
    background: {
      default: COLORS.BACKGROUND_WHITE,
    },
    text: {
      primary: COLORS.TEXT_PRIMARY,
      secondary: COLORS.TEXT_SECONDARY,
    },
  },
  typography: {
    fontFamily: FONTS.PRIMARY,
  },
};

// Shared feature configuration
export const features: FeatureConfig = {
  addressValidation: true,
  googleFonts: [FONTS.PRIMARY] as Array<string>,
  externalFonts: [
    {
      fontFamily: FONTS.HEADING,
      src: FONTS.HEADING_URL,
    },
  ],
};

// Shared modal styles
export const modalStyles = {
  modalTitle: {
    letterSpacing: 'normal',
    fontFamily: FONTS.HEADING,
  },
  primaryText: {
    letterSpacing: 'normal',
  },
  secondaryText: {
    letterSpacing: 'normal',
  },
  primaryButton: {
    borderRadius: '12px',
    letterSpacing: 'normal',
    fontWeight: 700,
  },
  secondaryButton: {
    letterSpacing: 'normal',
    fontWeight: 700,
  },
};

// Shared form styles
export const formStyles = {
  requiredInputIndicatorColor: COLORS.ERROR_RED,
  input: {
    borderRadius: '12px',
  },
  inputLabel: {
    fontSize: '14px',
    fontWeight: 700,
  },
  formSummaryDivider: {
    display: 'none',
  },
  formSummaryHeadersText: {
    fontWeight: '700',
  },
  formSummaryHeadersContainer: {
    backgroundColor: COLORS.BACKGROUND_LIGHT_GRAY,
  },
};

// Shared button styles
export const buttonStyles = {
  next: {
    backgroundColor: COLORS.PRIMARY_MAIN,
    color: COLORS.BACKGROUND_WHITE,
    border: 'none',
    borderRadius: '12px',
    padding: '12px 16px',
    fontSize: '16px',
    fontWeight: 700,
    height: '48px',
  },
  back: {
    backgroundColor: COLORS.BACKGROUND_WHITE,
    color: COLORS.TEXT_PRIMARY,
    border: `1px solid ${COLORS.BORDER_GRAY}`,
    borderRadius: '12px',
    padding: '12px 16px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    height: '48px',
  },
};

// Shared footer configuration
export const footerConfig: FooterConfig = {
  features: {
    footerBackNavigation: true,
    footerLocation: 'sticky',
  },
  style: {
    container: {
      backgroundColor: COLORS.BACKGROUND_WHITE,
      borderTop: `1px solid ${COLORS.BORDER_LIGHT}`,
      padding: '1rem 0',
    },
    content: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      margin: '0 5rem',
    },
    contentMobile: {
      margin: '0',
    },
    backButton: {
      ...buttonStyles.back,
      width: '164px',
    },
    nextButton: {
      ...buttonStyles.next,
      width: '164px',
    },
  },
};

// Mobile footer configuration
export const mobileFooterConfig: FooterConfig = {
  features: {
    footerBackNavigation: true,
  },
  style: {
    container: {
      margin: '0 -10px 0 -10px',
    },
    content: {
      display: 'flex',
      flexDirection: 'row',
      gap: '16px',
    },
    backButton: {
      ...buttonStyles.back,
      width: 'auto',
      flexGrow: 1,
    },
    nextButton: {
      ...buttonStyles.next,
      width: 'auto',
      flexGrow: 1,
    },
  },
};

// Shared header configuration
export const headerConfig: HeaderConfig = {
  features: {
    hidden: true,
  },
};

// Shared global style
export const globalStyle = {
  container: {
    fontFamily: FONTS.PRIMARY,
    padding: 0,
    display: 'flex',
    alignItems: 'flex-start',
  },
  content: {
    maxWidth: '762px',
  },
  title: {
    fontFamily: FONTS.HEADING,
    fontSize: '24px',
  },
};

// Shared loading indicator
export const initialLoadingIndicator = {
  style: {
    primaryColor: COLORS.PRIMARY_MAIN,
  },
};

// Mobile global style
export const mobileGlobalStyle = {
  container: {
    margin: '0',
    padding: '0',
    display: 'flex',
    alignItems: 'flex-start',
  },
  content: {
    width: '100%',
  },
  title: {
    fontFamily: FONTS.HEADING,
    fontSize: '24px',
  },
};
